import type { KeyboardEvent, RefObject } from 'react';
import { useCallback, useMemo } from 'react';
import type { InputComboboxProps, Option } from '../InputCombobox';

interface ComboboxField {
  onChange: (value: unknown) => void;
  value: unknown;
}

interface UseComboboxSelectionOptions<T> {
  choiceLimit?: number;
  closeButtonRef: RefObject<HTMLButtonElement | null>;
  disabled: boolean | undefined;
  field: ComboboxField;
  isMultiple: boolean;
  options?: Option<T>[];
  props: InputComboboxProps<T>;
  query: string;
  setQuery: (query: string) => void;
}

interface ComboboxSelection<T> {
  handleChange: (value: unknown) => void;
  handleBackspace: (event: KeyboardEvent<HTMLInputElement>) => void;
  handleRemove: (value: T) => void;
  selectedOptions: Option<T>[];
  selectedValue: T | undefined;
  selectedValues: T[];
}

export function useComboboxSelection<T>({
  choiceLimit,
  closeButtonRef,
  disabled,
  field,
  isMultiple,
  options,
  props,
  query,
  setQuery,
}: UseComboboxSelectionOptions<T>): ComboboxSelection<T> {
  const getOptionsFromValues = useCallback(
    (values: readonly T[]): Option<T>[] => {
      if (!options?.length) return [];

      return values
        .map((value) => options.find((option) => option.value === value))
        .filter((option): option is Option<T> => !!option);
    },
    [options],
  );
  const selectedValues = useMemo(
    () =>
      isMultiple && Array.isArray(field.value) ? (field.value as T[]) : [],
    [field.value, isMultiple],
  );
  const selectedValue = isMultiple ? undefined : (field.value as T | undefined);
  const selectedOptions = isMultiple
    ? getOptionsFromValues(selectedValues)
    : [];
  const notifyMultiSelectChange = useCallback(
    (values: T[]): void => {
      if (props.multiple) {
        props.onChange?.(getOptionsFromValues(values));
      }
    },
    [getOptionsFromValues, props],
  );
  const handleChange = useCallback(
    (value: unknown): void => {
      if (props.multiple) {
        const nextValues = Array.isArray(value) ? (value as T[]) : [];
        if (choiceLimit && nextValues.length > choiceLimit) return;

        field.onChange(nextValues);
        setQuery('');
        props.onChange?.(getOptionsFromValues(nextValues));

        if (choiceLimit && nextValues.length >= choiceLimit) {
          closeButtonRef.current?.click();
        }
        return;
      }

      field.onChange(value);
      setQuery('');
      props.onChange?.(options?.find((option) => option.value === value));
    },
    [
      choiceLimit,
      closeButtonRef,
      field,
      getOptionsFromValues,
      options,
      props,
      setQuery,
    ],
  );
  const handleBackspace = useCallback(
    (event: KeyboardEvent<HTMLInputElement>): void => {
      if (
        disabled ||
        event.key !== 'Backspace' ||
        query !== '' ||
        selectedValues.length === 0
      ) {
        return;
      }

      event.preventDefault();
      const nextValues = selectedValues.slice(0, -1);
      field.onChange(nextValues);
      notifyMultiSelectChange(nextValues);
    },
    [disabled, field, notifyMultiSelectChange, query, selectedValues],
  );
  const handleRemove = useCallback(
    (value: T): void => {
      if (disabled) return;

      const nextValues = selectedValues.filter(
        (selectedValue) => selectedValue !== value,
      );
      field.onChange(nextValues);
      notifyMultiSelectChange(nextValues);
    },
    [disabled, field, notifyMultiSelectChange, selectedValues],
  );

  return {
    handleChange,
    handleBackspace,
    handleRemove,
    selectedOptions,
    selectedValue,
    selectedValues,
  };
}
